/**
 * Production-safe logging utility for FieldIDX
 * 
 * CRITICAL: This platform handles sensitive athlete data.
 * Never log personal information, NIL valuations, recruiting scores,
 * or draft projections in production.
 */

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

// Extend Window type for Sentry
declare global {
  interface Window {
    Sentry?: {
      captureException: (error: unknown, context?: { extra?: Record<string, unknown> }) => void;
    };
  }
}

export const logger = {
  /**
   * Development-only logging
   * Use for debugging information that should never appear in production
   */
  dev: (...args: unknown[]) => {
    if (isDevelopment) {
      console.log('[DEV]', ...args);
    }
  },

  /**
   * Informational logging (development only)
   */
  info: (...args: unknown[]) => {
    if (isDevelopment) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Warning logging (development only)
   */
  warn: (...args: unknown[]) => {
    if (isDevelopment) {
      console.warn('[WARN]', ...args);
    }
  },

  /**
   * Error logging
   * Always logged, and should be sent to error tracking (Sentry)
   * DO NOT include sensitive athlete data in error logs
   */
  error: (message: string, error?: unknown, context?: Record<string, unknown>) => {
    // Sanitize context to remove sensitive data
    const sanitizedContext = context ? {
      ...context,
      // Remove potentially sensitive fields
      athleteData: context.athleteData ? '[REDACTED]' : undefined,
      playerData: context.playerData ? '[REDACTED]' : undefined,
      nilValuation: context.nilValuation ? '[REDACTED]' : undefined,
      recruitingScore: context.recruitingScore ? '[REDACTED]' : undefined,
      draftProjection: context.draftProjection ? '[REDACTED]' : undefined,
      profile: context.profile ? '[REDACTED]' : undefined,
      scores: context.scores ? '[REDACTED]' : undefined,
    } : undefined;

    console.error('[ERROR]', message, error);
    
    // In production, send to Sentry or other error tracking
    if (isProduction && typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureException(error || new Error(message), {
        extra: sanitizedContext,
      });
    }
  },

  /**
   * Performance logging (development only)
   */
  perf: (label: string, startTime?: number) => {
    if (isDevelopment) {
      if (startTime) {
        const duration = performance.now() - startTime;
        console.log(`[PERF] ${label}: ${duration.toFixed(2)}ms`);
      } else {
        console.log(`[PERF] ${label}`);
      }
    }
  },

  /**
   * Network request logging (development only)
   * DO NOT log request payloads that contain athlete data
   */
  network: (method: string, url: string, statusCode?: number) => {
    if (isDevelopment) {
      console.log(`[NETWORK] ${method} ${url}${statusCode ? ` - ${statusCode}` : ''}`);
    }
  },

  /**
   * Athlete data operation logging (development only, sanitized)
   * Only logs operation type and IDs, never actual data
   */
  athleteOp: (operation: string, athleteId?: string) => {
    if (isDevelopment) {
      console.log(`[ATHLETE] ${operation}${athleteId ? ` - ID: ${athleteId}` : ''}`);
    }
  },

  /**
   * Database operation logging (development only)
   */
  db: (operation: string, table: string, success: boolean = true) => {
    if (isDevelopment) {
      console.log(`[DB] ${operation} on ${table} - ${success ? 'success' : 'failed'}`);
    }
  },
};

// Export a type for the logger
export type Logger = typeof logger;

// Make it available globally for debugging in development
if (isDevelopment && typeof window !== 'undefined') {
  (window as Window & { logger?: typeof logger }).logger = logger;
}

/**
 * USAGE EXAMPLES:
 * 
 * ✅ GOOD - Safe logging:
 * logger.dev('Fetching athlete profile');
 * logger.athleteOp('fetch', athleteId);
 * logger.error('API call failed', error, { endpoint: '/api/athletes' });
 * 
 * ❌ BAD - Sensitive data logging:
 * logger.dev('Athlete data:', athleteProfile); // NEVER DO THIS
 * logger.dev('NIL valuation:', valuation); // NEVER DO THIS
 * console.log('Player scores:', scores); // NEVER DO THIS
 */
